import { Component, forwardRef, inject, input, OnInit } from '@angular/core';

import { ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AppType } from '../../../../core/models/app-types.model';
import { KeyValueInputComponent } from '../key-value-input/key-value-input.component';
import { KeyValue } from '../../../../core/models/app-types.model';
import { ButtonComponent } from '../../button/button.component';
import { LucideIconComponent } from '../../lucide-icon/lucide-icon.component';

@Component({
  selector: 'app-key-value-array-input',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    KeyValueInputComponent,
    ButtonComponent,
    LucideIconComponent
],
  templateUrl: './key-value-array-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KeyValueArrayInputComponent),
      multi: true,
    },
  ],
})
export class KeyValueArrayInputComponent
  implements ControlValueAccessor, OnInit
{
  private readonly fb = inject(FormBuilder);
  private sub = new Subscription();

  // --- Component API ---
  keys = input<AppType[]>([]);
  allowOther = input(true);
  addButtonLabel = input<string>('Add');

  // --- Internal State ---
  formArray = this.fb.array<FormControl<KeyValue | null>>([]);

  onChange: (value: (KeyValue | null)[]) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    if (this.formArray.length === 0) {
      this.addItem();
    }
  }

  writeValue(values: KeyValue[] | null): void {
    this.formArray.clear();
    if (values && values.length > 0) {
      values.forEach((value) => this.addItem(value));
    } else {
      this.addItem();
    }
  }

  registerOnChange(fn: (value: (KeyValue | null)[]) => void): void {
    this.onChange = fn;
    this.sub.add(
      this.formArray.valueChanges.subscribe((value) => {
        const filteredValue = value.filter((v) => v && (v.key || v.value));
        this.onChange(filteredValue.length > 0 ? filteredValue : []);
        this.onTouched();
      })
    );
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formArray.disable();
    } else {
      this.formArray.enable();
    }
  }

  addItem(value: KeyValue | null = null): void {
    this.formArray.push(this.fb.control(value));
  }

  removeItem(index: number): void {
    this.formArray.removeAt(index);
  }
}
